////////////////////////////////////////////////////////////////////////////////
//                                                                             /
//                                                                             /
//  (C) Copyright 2025 Autodesk, Inc. All rights reserved.                     /
//                                                                             /
//                       ****  CONFIDENTIAL MATERIAL  ****                     /
//                                                                             /
//  The information contained herein is confidential, proprietary to           /
//  Autodesk, Inc., and considered a trade secret.  Use of this information    /
//  by anyone other than authorized employees of Autodesk, Inc. is granted     /
//  only under a written nondisclosure agreement, expressly prescribing        /
//  the scope and manner of such use.                                          /
//                                                                             /
////////////////////////////////////////////////////////////////////////////////

import React, {useMemo, useState} from 'react';
import {
  CreateNewMenuItem,
  DefaultItem,
  Menu,
  MenuItem,
  MultiAutocompleteTokenControl,
  useCreatableItem,
  useMultiAutocomplete
} from "@adsk/alloy-react-dropdown";

const MultiSelectDropdown = ({items, selected, onSelectionChange, disabled, itemNormalizationFunction}: {
  items: DefaultItem[],
  selected?: DefaultItem[] | undefined,
  onSelectionChange?: (selected: DefaultItem[]) => void,
  disabled?: boolean,
  itemNormalizationFunction?: (item: DefaultItem) => DefaultItem
}) => {
  const [selection, setSelection] = useState(selected ?? []);
  const {getCreatableItemProps, creatableItem, ...autocompleteProps} = useCreatableItem({
    items: items
  });

  const {controlProps, menuProps: allMenuProps} = useMultiAutocomplete({
    initialIsOpen: false,
    selectedItems: selection,
    onSelectedItemsChange: changes => {
      const newSelection = changes.selectedItems?.map(i => {
        const normalized = itemNormalizationFunction == null ? i : itemNormalizationFunction(i);
        const builtIn = items.find(x => x.value === normalized.value);
        return builtIn ?? normalized;
      }) ?? [];
      setSelection(newSelection ?? []);
      if (onSelectionChange) {
        onSelectionChange(newSelection ?? []);
      }
    },
    ...autocompleteProps
  });

  const {
    menuItems,
    getItemProps,
    addSelectedItem,
    removeSelectedItem,
    ...menuProps
  } = allMenuProps;

  const renderedMenuItems = useMemo(() => menuItems.map((item, index) => {
      return item === creatableItem ? (
        <CreateNewMenuItem
          key={index}
          {...getCreatableItemProps()}
          {...getItemProps({item})}
        />
      ) : (
        <MenuItem
          key={index}
          {...getItemProps({item})}
        />
      )
    }),
    [creatableItem, getCreatableItemProps, getItemProps, menuItems]
  );

  return (
    <>
      <MultiAutocompleteTokenControl {...controlProps} disabled={disabled}/>
      <Menu {...menuProps}>{renderedMenuItems}</Menu>
    </>
  );
};

export default MultiSelectDropdown;